import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Form, Spinner } from 'react-bootstrap';
import FormPage from '../components/FormPage';
import FormCard from '../components/FormCard';
import FormField from '../components/FormField';
import FormActions from '../components/FormActions';
import CategoryMultiSelect from '../components/CategoryMultiSelect';
import { categoryService } from '../services/categoryService';
import { useTranslation } from '../contexts/LocaleContext';
import { ROUTES } from '../constants';

const CategoryEdit: React.FC = () => {
  const t = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [parentId, setParentId] = useState<number | undefined>();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (id) loadCategory();
  }, [id]);

  const loadCategory = async () => {
    if (!id) return;
    try {
      const category = await categoryService.getCategory(parseInt(id));
      setName(category.name);
      setParentId(category.parentCategoryId ?? undefined);
    } catch (error) {
      console.error('Error loading category:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id) return;
    try {
      await categoryService.updateCategory(parseInt(id), {
        name,
        parentCategoryId: parentId,
      });
      navigate(ROUTES.CATEGORIES);
    } catch (error) {
      console.error('Error updating category:', error);
      alert(t('errors.updateCategory'));
    }
  };

  if (!id || loading) {
    return (
      <FormPage title={t('categoryName')} backFallbackRoute={ROUTES.CATEGORIES}>
        <div className="d-flex justify-content-center my-4">
          <Spinner animation="border" role="status" />
        </div>
      </FormPage>
    );
  }

  return (
    <FormPage title={`${t('manage.edit')} ${t('categoryName')}`} backFallbackRoute={ROUTES.CATEGORIES}>
      <Form onSubmit={handleSubmit}>
        <FormCard>
          <FormField label={t('formField.name')} htmlFor="name" helper={t('helpers.category.name')}>
            <Form.Control
              type="text"
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
          </FormField>
          <FormField label={t('formField.parent')} htmlFor="parentCategory" helper={t('helpers.category.parentCategory')}>
            <CategoryMultiSelect
              value={parentId !== undefined ? [parentId] : []}
              onChange={(ids) => setParentId(ids[0])}
              excludeId={id ? parseInt(id) : undefined}
              mode="only"
              height="auto"
              maxHeight="200px"
            />
          </FormField>
          <FormActions submitLabel={t('manage.save')} backTo={ROUTES.CATEGORIES} backLabel={t('categories')} />
        </FormCard>
      </Form>
    </FormPage>
  );
};

export default CategoryEdit;
